// src/storage/syncEngine.ts — Local-First Cross-Device Synchronization Engine

import type {
  DomainDelta,
  SyncDomain,
  SyncPullRequest,
  SyncPullResponse,
  SyncPushRequest,
  SyncPushResponse,
  SyncStatusState,
} from './syncTypes';
import { AuthManager } from '../auth/authManager';

const SYNC_VERSIONS_KEY = 'clawey_sync_domain_versions_v1';

const DOMAIN_STORAGE_KEYS: Record<SyncDomain, string> = {
  comprehension: 'clawey_comprehension_progress_v1',
  spelling: 'clawey_spelling_progress_v1',
  sprint: 'clawey_sprint_progress_v1',
  mock: 'clawey_mock_exam_results_v1',
  points: 'clawey_explorer_points_v1',
  streak: 'clawey_daily_streak_v1',
  sessionGuard: 'clawey_session_guard_v1',
  telemetry: 'clawey_telemetry_logs_v1',
};

export class SyncEngine {
  private static status: SyncStatusState = 'synced';
  private static listeners: Array<(status: SyncStatusState) => void> = [];
  private static syncIntervalId: ReturnType<typeof setInterval> | null = null;
  private static isSyncing = false;

  /**
   * Initializes background sync listeners and timer.
   */
  static init(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('online', () => {
      this.syncNow().catch(() => {});
    });

    window.addEventListener('offline', () => {
      this.setStatus('offline');
    });

    // Auto-sync every 2 minutes when active
    if (!this.syncIntervalId) {
      this.syncIntervalId = setInterval(() => {
        if (navigator.onLine) {
          this.syncNow().catch(() => {});
        }
      }, 120000);
    }
  }

  static getStatus(): SyncStatusState {
    return this.status;
  }

  static onStatusChange(callback: (status: SyncStatusState) => void): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter((cb) => cb !== callback);
    };
  }

  /**
   * Full bidirectional sync: pushes local mutations, then pulls remote deltas and merges.
   */
  static async syncNow(): Promise<boolean> {
    if (this.isSyncing || !navigator.onLine) {
      if (!navigator.onLine) this.setStatus('offline');
      return false;
    }

    this.isSyncing = true;
    this.setStatus('syncing');

    try {
      const user = AuthManager.getUser();
      const token = AuthManager.getToken();
      const userId = user?.id || 'tudor-primary';

      // 1. Gather local deltas to push
      const localDeltas = this.collectLocalDeltas();
      if (localDeltas.length > 0) {
        const pushBody: SyncPushRequest = { userId, deltas: localDeltas };
        const pushRes = await fetch('/api/sync/push', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(pushBody),
        });

        if (pushRes.ok) {
          const pushData: SyncPushResponse = await pushRes.json();
          if (pushData.success) {
            this.recordSyncedVersions(localDeltas);
          }
        }
      }

      // 2. Pull remote deltas since current versions
      const domainVersions = this.getSavedVersions();
      const pullBody: SyncPullRequest = { userId, domainVersions };
      const pullRes = await fetch('/api/sync/pull', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(pullBody),
      });

      if (pullRes.ok) {
        const pullData: SyncPullResponse = await pullRes.json();
        if (pullData.success && pullData.deltas.length > 0) {
          this.mergeRemoteDeltas(pullData.deltas);
        }
      }

      this.setStatus('synced');
      this.isSyncing = false;
      return true;
    } catch {
      this.setStatus(navigator.onLine ? 'error' : 'offline');
      this.isSyncing = false;
      return false;
    }
  }

  /**
   * Domain-specific merge algorithm resolving remote deltas with local storage.
   */
  static mergeRemoteDeltas(remoteDeltas: DomainDelta[]): void {
    const versions = this.getSavedVersions();

    for (const delta of remoteDeltas) {
      const storageKey = DOMAIN_STORAGE_KEYS[delta.domain];
      if (!storageKey) continue;

      try {
        const localRaw = localStorage.getItem(storageKey);
        const localData = localRaw ? JSON.parse(localRaw) : null;
        const mergedData = this.resolveDomainConflict(delta.domain, localData, delta.payload);

        localStorage.setItem(storageKey, JSON.stringify(mergedData));
        versions[delta.domain] = delta.version;
      } catch (err) {
        console.error(`Error merging delta for ${delta.domain}:`, err);
      }
    }

    try {
      localStorage.setItem(SYNC_VERSIONS_KEY, JSON.stringify(versions));
    } catch {
      // ignore
    }
  }

  /**
   * Resolves conflicts cleanly by domain rules.
   */
  static resolveDomainConflict(
    domain: SyncDomain,
    local: any,
    remote: any
  ): any {
    if (!local) return remote;
    if (!remote) return local;

    switch (domain) {
      case 'comprehension': {
        const solvedPassageIds = Array.from(
          new Set([...(local.solvedPassageIds || []), ...(remote.solvedPassageIds || [])])
        );
        const masteredVocabWords = Array.from(
          new Set([...(local.masteredVocabWords || []), ...(remote.masteredVocabWords || [])])
        );
        return {
          ...local,
          ...remote,
          solvedPassageIds,
          masteredVocabWords,
          streakCount: Math.max(local.streakCount || 0, remote.streakCount || 0),
          bestStreak: Math.max(local.bestStreak || 0, remote.bestStreak || 0),
          totalCluesFound: Math.max(local.totalCluesFound || 0, remote.totalCluesFound || 0),
        };
      }

      case 'points': {
        return {
          totalPoints: Math.max(local.totalPoints || 0, remote.totalPoints || 0),
          lifetimeEarned: Math.max(local.lifetimeEarned || 0, remote.lifetimeEarned || 0),
          lastUpdated: Math.max(local.lastUpdated || 0, remote.lastUpdated || 0),
        };
      }

      case 'streak': {
        return {
          currentStreak: Math.max(local.currentStreak || 0, remote.currentStreak || 0),
          bestStreak: Math.max(local.bestStreak || 0, remote.bestStreak || 0),
          lastPracticedDate:
            (local.lastPracticedDate || '') > (remote.lastPracticedDate || '')
              ? local.lastPracticedDate
              : remote.lastPracticedDate,
        };
      }

      case 'sessionGuard': {
        // Remote parent limit takes authority; daily minutes takes max
        return {
          dailyLimitMinutes: remote.dailyLimitMinutes ?? local.dailyLimitMinutes ?? 30,
          todayMinutes: Math.max(local.todayMinutes || 0, remote.todayMinutes || 0),
          lastResetDate: remote.lastResetDate || local.lastResetDate,
        };
      }

      default:
        // Default to latest payload
        return { ...local, ...remote };
    }
  }

  private static collectLocalDeltas(): DomainDelta[] {
    const deltas: DomainDelta[] = [];
    const versions = this.getSavedVersions();

    for (const [domainStr, storageKey] of Object.entries(DOMAIN_STORAGE_KEYS)) {
      const domain = domainStr as SyncDomain;
      try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
          const payload = JSON.parse(raw);
          const currentVersion = (versions[domain] || 0) + 1;
          deltas.push({
            domain,
            version: currentVersion,
            payload,
            clientUpdatedAt: Date.now(),
          });
        }
      } catch {
        // Ignore unparseable keys
      }
    }

    return deltas;
  }

  private static getSavedVersions(): Partial<Record<SyncDomain, number>> {
    try {
      const raw = localStorage.getItem(SYNC_VERSIONS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  private static recordSyncedVersions(deltas: DomainDelta[]): void {
    const versions = this.getSavedVersions();
    for (const d of deltas) {
      versions[d.domain] = d.version;
    }
    try {
      localStorage.setItem(SYNC_VERSIONS_KEY, JSON.stringify(versions));
    } catch {
      // ignore
    }
  }

  private static setStatus(status: SyncStatusState): void {
    this.status = status;
    this.listeners.forEach((cb) => {
      try {
        cb(status);
      } catch (e) {
        console.error('Sync listener error:', e);
      }
    });
  }
}
